import * as React from 'react';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import MuiCheckbox from '@mui/material/Checkbox';
import {FieldInput} from './FieldInput';
import {makeSxStyles, MuiSxProps} from '../utils/sx-helpers';

const makeUseStyles = ({
  noWrap,
  disabled,
}: {
  noWrap: boolean;
  disabled: boolean;
}) =>
  makeSxStyles(theme => ({
    label: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      width: (() => (noWrap ? 0 : undefined))(),
      flex: (() => (noWrap ? 1 : undefined))(),
    },
    text: {
      color: (() =>
        disabled ? theme.palette.text.disabled : theme.palette.text.primary)(),
    },
  }));

const SxLabel = styled('label')({});

const StyledCheckbox = styled(MuiCheckbox)(({theme}) => ({
  padding: '3px',
  marginRight: theme.spacing(2),
}));

type CheckboxProps = React.ComponentProps<typeof MuiCheckbox> & {
  checked?: boolean;
  label?: React.ReactNode;
  labelProps?: React.ComponentProps<typeof Typography>;
  /** Custom typography component to use for the label */
  LabelComponent?: React.ComponentType;
  /** Custom Checkbox component to use for the box */
  CheckboxComponent?: typeof MuiCheckbox;
  noWrap?: boolean;
  sx?: MuiSxProps;
};

export const Checkbox: React.FC<CheckboxProps> = ({
  name,
  label,
  color = 'primary',
  labelProps = {variant: 'body2', component: 'span'},
  LabelComponent,
  CheckboxComponent,
  noWrap = false,
  sx = {},
  ...props
}) => {
  const [styles] = makeUseStyles({noWrap, disabled: Boolean(props.disabled)})();
  const CheckboxRoot = CheckboxComponent ?? StyledCheckbox;

  const LabelRoot =
    LabelComponent ??
    ((({children}) => (
      <Typography
        {...labelProps}
        className="label"
        sx={styles.text}
        noWrap={noWrap}
      >
        {children}
      </Typography>
    )) as React.FC<React.PropsWithChildren>);

  return (
    <SxLabel sx={{...styles.label, ...sx}} htmlFor={name}>
      <CheckboxRoot className="input" id={name} color={color} {...props} />
      <LabelRoot>{label}</LabelRoot>
    </SxLabel>
  );
};

export const FieldCheckboxGroup: React.FC<{
  checkboxes: CheckboxProps[];
}> = ({checkboxes}) => (
  <>
    {checkboxes.map((props: CheckboxProps) => (
      <FieldInput
        key={props.name}
        {...props}
        type="checkbox"
        component={Checkbox}
      />
    ))}
  </>
);
